var template = require('@/modules/components/playlist/grid.handlebars')
const shortVideoTemplate = require('@/modules/components/lora/short-video.handlebars')
import { MIN_INDEX, PAGE_SIZE, SHORT_VIDEO_TRACKING_TYPE } from '@/configs/constants'
import { STATUS_RESPONSES, MANUAL_CART_EVENTS, DEFAULT_MIN_HEIGHT_TO_SHOW_BTN } from '@/configs/settings'
import GlobalSettings from '@/core/global-settings'
import { debounce, initialObserver, appendCommonStyling } from '@/helpers'
import httpResources from '@/libs/axios'
import { updatePlaylistData } from '~/store/playlist'
import EventManager from '@/core/event-manager'
import { v4 as uuidv4 } from 'uuid'

class LoraGridPlaylist extends HTMLElement {
    constructor() {
        super()
        this.attachShadow({ mode: 'open' })
        this.httpInstance = GlobalSettings.httpInstance || httpResources
        this.handlebarTemplate = template
        this.itemElements = null
        this.videos = []
        this.eventManager = new EventManager()
        this.uuid = uuidv4()
        this.organizationId = null
        this.customTagName = ''

        this.params = {
            pageSize: PAGE_SIZE,
            pageNum: 1,
            totalPages: 0,
            isEnded: false,
        }
        this.shortVideoTemplate = shortVideoTemplate
    }

    getDataAttributes() {
        this.ctaMode = this.getAttribute('cta')
        this.playlistId = this.getAttribute('playlist-id')
        this.enableFullscreen = this.getAttribute('enable-fullscreen') == 'false' ? false : true
        this.gutters = this.getAttribute('gutters') ? parseFloat(this.getAttribute('gutters')) : 10
        this.maxVideos = this.getAttribute('max-videos') ? parseInt(this.getAttribute('max-videos')) : 0 // 0 is infinitive

        this.itemsPerRow = this.getAttribute('item-per-row') ? parseInt(this.getAttribute('item-per-row')) : 6
        this.itemsPerRowTablet = this.getAttribute('item-per-row-tablet')
            ? parseInt(this.getAttribute('item-per-row'))
            : 3
        this.itemsPerRowMobile = this.getAttribute('item-per-row-mobile')
            ? parseInt(this.getAttribute('item-per-row'))
            : 2
        this.isForceNewTab =
            this.hasAttribute('fs-force-new-tab') && this.getAttribute('fs-force-new-tab') !== 'false' ? true : false
        this.loop = this.getAttribute('loop') !== null && this.getAttribute('loop') !== 'false' ? true : false
        this.group = this.getAttribute('group') ? this.getAttribute('group') : 'general'
        this.pipStyle = this.getAttribute('pip-style') || 'bottom: 0px; right: 15px;'
        this.isSimple = this.getAttribute('simple') !== null ? true : false
        this.videoHeight = this.getAttribute('video-height') ? parseFloat(this.getAttribute('video-height')) : 0 // 0 is 100% followed the parent layout
        this.currentItemsPerRow = this.itemsPerRow
        this.minVideoHeightToShowButtons = this.getAttribute('min-h-btns') || DEFAULT_MIN_HEIGHT_TO_SHOW_BTN
    }

    async fetchData() {
        if (this.params.isEnded) return
        if (this.httpInstance.repositories) {
            try {
                const { code, data } = await this.httpInstance.repositories.shortVideo.playlistDetail(
                    this.playlistId,
                    this.params.pageSize,
                    this.params.pageNum
                )
                if (code === STATUS_RESPONSES.SUCCESS) {
                    this.organizationId = data.organizationId
                    this.params.totalPages = data.shortVideos.totalPages
                    this.params.pageNum++

                    if (this.params.pageNum > this.params.totalPages) {
                        this.params.isEnded = true
                    }
                    data.shortVideos = {
                        ...data.shortVideos,
                        data: data.shortVideos?.data.map((e) => ({
                            ...e,
                            organizationId: this.organizationId,
                            tagName: this.customTagName,
                            pipStyle: this.pipStyle,
                            uuid: this.uuid,
                            enableFullscreen: this.enableFullscreen,
                            minVideoHeightToShowButtons: this.minVideoHeightToShowButtons,
                        })),
                    }
                    let dataForTracking = data.shortVideos.data
                    if (this.params.pageNum > 2 && this.maxVideos) {
                        const currentVideos = this.videos.length
                        const remainingVideos = this.maxVideos - currentVideos
                        const videosToRender = Math.min(data.shortVideos.data.length, remainingVideos)
                        this.renderMore(data.shortVideos.data.slice(0, videosToRender))
                        dataForTracking = data.shortVideos.data.slice(0, videosToRender)
                    } else if (this.params.pageNum > 2) {
                        this.renderMore(data.shortVideos.data)
                        dataForTracking = data.shortVideos.data
                    }

                    if (this.maxVideos) {
                        this.videos = [...this.videos, ...data.shortVideos.data]?.slice(0, this.maxVideos)
                        dataForTracking = this.videos
                    } else {
                        this.videos = [...this.videos, ...data.shortVideos.data]
                    }
                    this.setAttribute('max-index', this.videos.length - 1)

                    const videoEmbeds = this.shadowRoot.querySelectorAll('video-embed')
                    // this.videos.forEach((video, index) => {
                    // 	videoEmbeds[index]?.setShortVideoDataFromPlaylist(video);
                    // });

                    // if (!this.fullscreenMode && dataForTracking.length) {
                    //     GlobalSettings.trackingIOT(
                    //         SHORT_VIDEO_TRACKING_TYPE.IMPRESSION,
                    //         this.playlistId,
                    //         dataForTracking,
                    //         this.shadowRoot.host.tagName,
                    //         data.organizationId
                    //     )
                    // }
                    if (this.params.pageNum <= 2) {
                        this.render()
                    }

                    updatePlaylistData(this.playlistId, {
                        pageSize: this.params.pageSize,
                        pageNum: this.params.pageNum - 1,
                        isEnded: this.params.isEnded,
                        videos: [...this.videos],
                        data,
                    })

                    const elementObserver = this.itemElements[this.videos.length - 1]
                    initialObserver(
                        elementObserver,
                        (entries, observer) => {
                            entries.forEach((entry, index) => {
                                if (entry.isIntersecting) {
                                    observer.unobserve(elementObserver)
                                    this.fetchData()
                                }
                            })
                        },
                        { threshold: 0 }
                    )
                }
            } catch (e) {
                console.error(e)
            }
        }
    }

    connectedCallback() {
        this.customTagName = this.shadowRoot.host.tagName
        this.getDataAttributes()
        this.fetchData()
    }

    windowResizeEvent() {
        window.addEventListener(
            'resize',
            debounce(() => {
                this.shadowRoot.querySelector('.lora-grid__inner').style.width = `100%`
                window.requestAnimationFrame(() => {
                    this.calculatorItemLayout()
                })
            }, 300)
        )
    }

    calculatorItemLayout() {
        let gridWidth = this.shadowRoot.querySelector('.lora-grid__inner').clientWidth
        let maxScreenHeight = window.innerHeight - 2 * this.gutters
        this.currentItemsPerRow = this.itemsPerRow
        if (gridWidth > 1024) {
            // desktop
            this.currentItemsPerRow = this.itemsPerRow
        } else if (gridWidth > 768) {
            // tablet
            this.currentItemsPerRow = this.itemsPerRowTablet
        } else {
            // phone
            this.currentItemsPerRow = this.itemsPerRowMobile
        }
        this.shadowRoot.querySelector('.lora-grid').style.setProperty('--items-per-row', `${this.currentItemsPerRow}`)
        let itemWidthOuter = 0

        if (this.videoHeight) {
            // Custom height
            this.itemWidth = (this.videoHeight * 9) / 16
            itemWidthOuter = this.itemWidth + 2 * this.gutters
            if (gridWidth < itemWidthOuter * this.currentItemsPerRow) {
                this.itemWidth = 0
            } else {
                let maxWidthContainer = itemWidthOuter * this.currentItemsPerRow
                this.shadowRoot.querySelector('.lora-grid__inner').style.width = `${maxWidthContainer}px`
                gridWidth = maxWidthContainer
            }
        }

        // Re-check width - height
        let tmpItemWidthOuter = gridWidth / this.currentItemsPerRow
        let tmpItemHeight = (tmpItemWidthOuter * 16) / 9
        if (tmpItemHeight > maxScreenHeight) {
            this.videoHeight = maxScreenHeight
            this.itemWidth = (this.videoHeight * 9) / 16
            itemWidthOuter = this.itemWidth + 2 * this.gutters
        }
        this.itemElements = this.shadowRoot.querySelectorAll('.lora-grid__item')

        this.itemElements.forEach((itemElement, index) => {
            let videoEmbed = itemElement.querySelector('video-embed')
            if (this.itemWidth) {
                itemElement.style.width = `${itemWidthOuter}px`
                videoEmbed.setAttribute('custom-height', true)
                videoEmbed.setAttribute('video-height', this.videoHeight)
            } else {
                videoEmbed.removeAttribute('custom-height')
            }
            if (index === 0 && videoEmbed) {
                videoEmbed.setAttribute('autoplay', '')
            }
        })

        this.shadowRoot.addEventListener('update-sound-status', (data) => {
            console.log('update-sound-status', data)
        })
    }

    registerEvents() {
        this.windowResizeEvent()
        this.calculatorItemLayout()
    }

    render() {
        var context = {
            playlistId: this.playlistId,
            videos: this.videos,
            ctaMode: this.ctaMode,
            gutters: `${this.gutters}px`,
            loop: this.loop,
            itemsPerRow: this.itemsPerRow,
            itemsPerRowTablet: this.itemsPerRowTablet,
            itemsPerRowMobile: this.itemsPerRowMobile,
            group: this.group,
            single: this.isSimple,
            maxVideos: this.maxVideos,
            pipStyle: this.pipStyle,
            uuid: this.uuid,
            organizationId: this.organizationId,
            tagName: this.customTagName,
            enableFullscreen: this.enableFullscreen,
            isForceNewTab: this.isForceNewTab,
            minVideoHeightToShowButtons: this.minVideoHeightToShowButtons,
            videoType: this.overlayType,
        }
        this.shadowRoot.innerHTML = `${this.handlebarTemplate(context)}`
        appendCommonStyling(this.shadowRoot)
        this.registerEvents()
        this.handleInternalEvents()
    }

    handleInternalEvents() {
        window.addEventListener('OPEN_FULLSCREEN', (event) => {
            const { playlistId, videoId, uuid } = event?.detail
            if (this.uuid === uuid) {
                const fullscreenElm = document.querySelector('b-fullscreen')
                this.fullscreenInternalEvents(fullscreenElm)
                this.itemElements.forEach((itemElement) => {
                    let videoEmbed = itemElement.querySelector('video-embed')
                    videoEmbed.setAttribute('disable-tracking', true)
                })
            }
        })
        window.addEventListener('EXIT_FULLSCREEN', (event) => {
            const { playlistId, videoId, uuid } = event?.detail
            if (this.uuid === uuid) {
                this.itemElements.forEach((itemElement) => {
                    let videoEmbed = itemElement.querySelector('video-embed')
                    videoEmbed.setAttribute('disable-tracking', false)
                })
            }
        })
    }

    fullscreenInternalEvents(el) {
        el.eventManager.on(MANUAL_CART_EVENTS.SYNC_PRODUCT, (data, callback) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.SYNC_PRODUCT, data, callback)
        })

        el.eventManager.on(MANUAL_CART_EVENTS.ADD_TO_CART, (data, callback) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.ADD_TO_CART, data, callback)
        })

        el.eventManager.on(MANUAL_CART_EVENTS.UPDATE_ITEM_IN_CART, (data, callback) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.UPDATE_ITEM_IN_CART, data, callback)
        })

        el.eventManager.on(MANUAL_CART_EVENTS.SYNC_CART_STATE, (data, callback) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.SYNC_CART_STATE, data, callback)
        })

        el.eventManager.on(MANUAL_CART_EVENTS.CHECKOUT, (data, callback) => {
            this.eventManager.emit(MANUAL_CART_EVENTS.CHECKOUT, data, callback)
        })
    }

    renderMore(videos) {
        const innerList = this.shadowRoot.querySelector('.lora-grid__inner')
        if (!innerList) return
        videos.forEach((v, index) => {
            const context = {
                ...v,
                videoId: v.id,
                videoType: v.overlayType,
            }
            const wrapperCarouselItem = document.createElement('div')
            wrapperCarouselItem.classList = 'lora-grid__item'
            wrapperCarouselItem.setAttribute('data-index', this.videos.length + index)

            const carouselItem = document.createElement('video-embed')
            carouselItem.classList = 'lora-story-block__video-embed'
            carouselItem.setAttribute('video-id', v.id)
            carouselItem.setAttribute('title', v.title)
            carouselItem.setAttribute('data-index', this.videos.length + index)
            carouselItem.setAttribute('group', '')
            carouselItem.setAttribute('gutters', '')
            carouselItem.setAttribute('lazyload', '')
            carouselItem.setAttribute('cta', '')
            carouselItem.setAttribute('wrapper', 'lora-grid-playlist')
            carouselItem.setAttribute('item-wrapper', '.lora-grid__item')
            carouselItem.setAttribute('thumbnail', v.thumbnail)
            carouselItem.setAttribute('story', '')
            carouselItem.setAttribute('dynamic-height', '')
            carouselItem.setAttribute('simple', '')
            carouselItem.setAttribute('no-action-hover', '')
            carouselItem.setAttribute('max-videos', this.maxVideos)
            carouselItem.setAttribute('video-type', v.overlayType)
            carouselItem.setAttribute('pip-style', v.pipStyle)
            carouselItem.setAttribute('tag-name', v?.tagName)
            carouselItem.setAttribute('organization-id', v?.organizationId)
            carouselItem.setAttribute('enable-fullscreen', v?.enableFullscreen)
            carouselItem.setAttribute('uuid', v?.uuid)
            carouselItem.setAttribute('min-h-btns', v?.minVideoHeightToShowButtons)
            carouselItem.setAttribute('enable-like-button', v?.enableLikeButton)
            carouselItem.setAttribute('display-like-count', v?.displayLikeCount)
            carouselItem.setAttribute('total-like', v?.totalLike)
            carouselItem.innerHTML = `${this.shortVideoTemplate(context)}`
            wrapperCarouselItem.appendChild(carouselItem)

            innerList.appendChild(wrapperCarouselItem)
        })
        this.registerEvents()
    }
}
window.customElements.define('grid-playlist', LoraGridPlaylist)
